import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
// Use the shared ThemeProvider to keep toggle behavior consistent with Header
import { useTheme } from "@/components/theme/ThemeProvider";
import { StarBackground } from "@/components/ui/StarBackground";
import {
  ArrowRight,
  Check,
  ChevronRight,
  Sun,
  Moon,
  Menu,
  Shield,
  BarChart3,
  Users,
  Mail,
  Phone,
  MapPin,
  MessageSquare,
} from "lucide-react";
import Features from "./Features";
import Pricing from "./Pricing";
import LandingFooter from "../LandingFooter";
import dashboardDark from "./assets/soldark.png";
import dashboardLight from "./assets/sollight.png";
import sonomaLogo from "./assets/sonoma-rounded.png";
import heroIllustration from "./assets/sol.png";

import { useSelector } from "react-redux";
import { selectUsers } from "@/State/usersSlice";

export default function Home() {
  const navigate = useNavigate();
  const { currentUser } = useSelector(selectUsers);

  useEffect(() => {
    // Ultra-Durable Fix: Never show landing page to a logged-in user.
    // Check 1: Redux state
    if (currentUser && currentUser.selectedAffiliation) {
      navigate('/dashboard', { replace: true });
      return;
    }
    
    // Check 2: Fallback - localStorage cached auth (handles race conditions)
    try {
      const cachedAuth = localStorage.getItem('cachedAuth');
      if (cachedAuth) {
        const parsed = JSON.parse(cachedAuth);
        if (parsed && parsed.uid) {
          console.log('Home: Found cached auth, redirecting to dashboard');
          navigate('/dashboard', { replace: true });
          return;
        }
      }
    } catch (e) {
      // Parsing failed, continue rendering landing page
    }
  }, [currentUser, navigate]);

  const { theme, setTheme } = useTheme();
  const [resolvedTheme, setResolvedTheme] = useState<"light" | "dark">("light");
  // Click-origin circle reveal state for smooth, performant theme transitions
  const timeoutsRef = useRef<number[]>([]);
  const [circleVisible, setCircleVisible] = useState(false);
  const [circleScaled, setCircleScaled] = useState(false);
  const [circleProps, setCircleProps] = useState({ left: 0, top: 0, size: 24, scale: 1, color: 'rgba(0,0,0,0.65)' });
  const [loginOpen, setLoginOpen] = useState(false);

  useEffect(() => {
    if (theme === "system") {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
      setResolvedTheme(systemTheme);
    } else {
      setResolvedTheme(theme);
    }
  }, [theme]);

  // Smooth theme toggle with radial reveal from click point
  const toggleThemeWithTransition = (e?: React.MouseEvent) => {
    const newTheme = resolvedTheme === "dark" ? "light" : "dark";

    // get click position (fallback to center)
    const clickX = e && 'clientX' in e ? e.clientX : window.innerWidth / 2;
    const clickY = e && 'clientY' in e ? e.clientY : window.innerHeight / 2;

    const color = newTheme === "dark" ? "rgba(0,0,0,0.75)" : "rgba(255,255,255,0.92)";

    // compute required scale so the expanding circle covers the viewport
    const initialSize = 24; // px
    const maxDX = Math.max(clickX, window.innerWidth - clickX);
    const maxDY = Math.max(clickY, window.innerHeight - clickY);
    const requiredRadius = Math.sqrt(maxDX * maxDX + maxDY * maxDY);
    const finalScale = (requiredRadius * 2) / initialSize + 0.2;

    // clear previous timeouts
    timeoutsRef.current.forEach((t) => window.clearTimeout(t));
    timeoutsRef.current = [];

    setCircleProps({ left: clickX - initialSize / 2, top: clickY - initialSize / 2, size: initialSize, scale: finalScale, color });
    setCircleVisible(true);

    // start expand next tick so the browser applies initial styles
    const t1 = window.setTimeout(() => setCircleScaled(true), 20) as unknown as number;
    timeoutsRef.current.push(t1);

    // switch theme slightly after expansion starts to hide repaint
    const t2 = window.setTimeout(() => setTheme(newTheme), 110) as unknown as number;
    timeoutsRef.current.push(t2);

    // collapse and hide the circle after animation
    const t3 = window.setTimeout(() => {
      setCircleScaled(false);
      const t4 = window.setTimeout(() => setCircleVisible(false), 260) as unknown as number;
      timeoutsRef.current.push(t4);
    }, 420) as unknown as number;
    timeoutsRef.current.push(t3);
  };

  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach((t) => window.clearTimeout(t));
      timeoutsRef.current = [];
    };
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#020817] text-slate-950 dark:text-slate-50 overflow-x-hidden">
      <StarBackground />
      {/* Click-origin reveal circle (fade originates from click) */}
      {circleVisible && (
        <div
          aria-hidden="true"
          className="fixed z-[60] pointer-events-none"
          style={{
            left: circleProps.left,
            top: circleProps.top,
            width: circleProps.size,
            height: circleProps.size,
            borderRadius: '50%',
            background: circleProps.color,
            transform: circleScaled ? `scale(${circleProps.scale})` : 'scale(1)',
            transition: 'transform 360ms cubic-bezier(.2,.9,.2,1), opacity 220ms ease',
            opacity: circleScaled ? 1 : 1,
            willChange: 'transform, opacity',
          }}
        />
      )}
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-[#020817]/80 backdrop-blur-xl border-b border-slate-200/30 dark:border-slate-800/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-2.5">
              <img 
                src={sonomaLogo}
                alt="Sonoma Cloud" 
                className="w-8 h-8 dark:drop-shadow-[0_0_10px_rgba(255,255,255,0.25)]"
              />
              <span className="text-lg font-semibold tracking-tight">Sonoma Cloud</span>
            </div>

            {/* Nav Links */}
            <div className="hidden md:flex items-center gap-8">
              <button 
                onClick={() => scrollToSection('features')}
                className="text-sm text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-50 transition-colors"
                data-testid="nav-features"
              >
                Features
              </button>
              <button 
                onClick={() => scrollToSection('workflows')}
                className="text-sm text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-50 transition-colors"
                data-testid="nav-workflows"
              >
                Workflows
              </button>
              <button 
                onClick={() => scrollToSection('pricing')}
                className="text-sm text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-50 transition-colors"
                data-testid="nav-pricing"
              >
                Pricing
              </button>
              <button 
                onClick={() => scrollToSection('faq')}
                className="text-sm text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-50 transition-colors"
                data-testid="nav-faq"
              >
                FAQ
              </button>
            </div>
            
            {/* Actions */}
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleThemeWithTransition}
                data-testid="button-theme-toggle"
                className="rounded-full w-10 h-10 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-900 dark:text-slate-50 transition-all duration-300"
                aria-label="Toggle theme"
              >
                {resolvedTheme === "dark" ? (
                  <Moon className="h-5 w-5 text-blue-400" />
                ) : (
                  <Sun className="h-5 w-5 text-orange-500" />
                )}
              </Button>

              {/* Login dropdown */}
              <div
                className="relative hidden sm:inline-flex"
                onMouseEnter={() => setLoginOpen(true)}
                onMouseLeave={() => setLoginOpen(false)}
                onFocus={() => setLoginOpen(true)}
                onBlur={() => setLoginOpen(false)}
              >
                <Button variant="ghost" className="text-sm font-medium hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200" data-testid="button-login" aria-haspopup="menu" aria-expanded={loginOpen}>
                  Login
                </Button>

                <div
                  className={`absolute right-0 mt-2 w-48 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#020817] shadow-lg transform transition-all duration-150 origin-top-right ${loginOpen ? 'opacity-100 scale-100 pointer-events-auto' : 'opacity-0 scale-95 pointer-events-none'}`}
                >
                  <Link role="menuitem" tabIndex={-1} to="/admin-login" className="block px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800">School Admin</Link>
                  <Link role="menuitem" tabIndex={-1} to="/faculty-login" className="block px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800">Faculty Staff</Link>
                  <Link role="menuitem" tabIndex={-1} to="/guardian-login" className="block px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800">Guardian</Link>
                  <Link role="menuitem" tabIndex={-1} to="/student-login" className="block px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800">Student</Link>
                </div>
              </div>

              <Button className="hidden sm:inline-flex text-sm font-medium px-5 h-10 bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-600 dark:text-white dark:hover:bg-blue-500 border-transparent shadow-md shadow-blue-500/20 transition-all hover:shadow-lg hover:shadow-blue-500/30" data-testid="button-get-started-nav">
                GET STARTED
              </Button>
              
              <Button variant="ghost" size="icon" className="md:hidden hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-900 dark:text-slate-50" data-testid="button-mobile-menu">
                <Menu className="h-6 w-6" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-44 lg:pb-32 px-6 lg:px-8 relative overflow-hidden">
        {/* Gradient glow effect */}
        <div className="absolute top-0 right-0 w-[800px] h-[600px] pointer-events-none">
          <div className="absolute top-20 right-[-100px] w-[600px] h-[400px] bg-gradient-to-bl from-cyan-500/25 via-sky-400/15 to-transparent rounded-full blur-3xl transform rotate-12"></div>
          <div className="absolute top-40 right-[100px] w-[300px] h-[300px] bg-gradient-to-br from-sky-500/20 via-cyan-500/10 to-transparent rounded-full blur-2xl"></div>
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
            {/* Left side - Text content */}
            <div className="max-w-xl">
              {/* Badge removed as requested */}
              
              {/* Headline */}
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.15] mb-8"
              >
                Run your school<br />
                with <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500 dark:from-blue-400 dark:to-cyan-300 italic">clarity.</span>
              </motion.h1>
              
              {/* Subheadline */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-base sm:text-lg text-slate-500 dark:text-slate-400 max-w-xl mb-10 leading-relaxed"
              >
                The bursar has the ledger. The principal has the files. The teachers 
                have the mark sheets. Sonoma connects them all into one calm, 
                reliable operating system.
              </motion.p>
              
              {/* CTAs */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="flex flex-col sm:flex-row gap-3"
              >
                <Button size="lg" className="text-sm font-medium rounded-lg px-6 h-11 bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-600 dark:text-white dark:hover:bg-blue-500 shadow-lg shadow-blue-500/20" data-testid="button-get-started-hero">
                  Explore Demo
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button size="lg" variant="outline" className="text-sm font-medium rounded-lg px-6 h-11 bg-white dark:bg-slate-950 border-slate-200 text-slate-900 hover:bg-slate-100 dark:border-slate-800 dark:text-slate-50 dark:hover:bg-slate-800" data-testid="button-book-demo">
                  Talk to Us
                </Button>
              </motion.div>
            </div>
            
            {/* Right side - Hero illustration */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="hidden lg:block"
            >
              <img 
                src={heroIllustration}
                alt="School management platform illustration"
                className="w-full max-w-lg ml-auto rounded-2xl"
                data-testid="hero-illustration"
              />
            </motion.div>
          </div>
          
          {/* Dashboard Preview */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="mt-20 lg:mt-24"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-t from-white dark:from-[#020817] via-transparent to-transparent z-10 pointer-events-none h-40 bottom-0 top-auto"></div>
              <div className="rounded-xl overflow-hidden border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#020817] shadow-2xl">
                <img 
                  src={resolvedTheme === 'dark' ? dashboardDark : dashboardLight}
                  alt="Sonoma Dashboard"
                  className="w-full"
                  data-testid="dashboard-screenshot"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 lg:py-28 px-6 lg:px-8 bg-slate-50/50 dark:bg-slate-900/50">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true, margin: "-50px" }}
            className="mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3 text-slate-900 dark:text-slate-50">Our progress</h2>
            <p className="text-lg text-slate-600 dark:text-slate-400">Trusted by schools across Africa and beyond</p>
          </motion.div>
          
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Left - Chart visualization */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
              viewport={{ once: true, margin: "-50px" }}
              className="bg-white dark:bg-[#020817] rounded-2xl p-8 border border-slate-200 dark:border-slate-800 shadow-sm"
            >
              <h3 className="text-xl font-semibold mb-2 text-slate-900 dark:text-slate-50">Monthly Active Schools</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-8">Schools with at least one activity over the last 30 days</p>
              
              {/* Chart area */}
              <div className="flex items-end justify-between gap-2 h-64 mb-6">
                {[60, 75, 65, 90, 110, 100, 125, 140, 130, 160, 170].map((value, i) => (
                  <div
                    key={i}
                    className="flex-1 bg-gradient-to-t from-blue-600 to-cyan-400 dark:from-blue-600 dark:to-cyan-400 rounded-t-md min-w-[20px] opacity-90 hover:opacity-100 transition-opacity"
                    style={{ height: `${Math.round((value / 170) * 100)}%` }}
                  />
                ))}
              </div>
              
              {/* X-axis labels */}
              <div className="flex justify-between text-xs font-medium text-slate-500 dark:text-slate-400 mb-6">
                <span>Jan</span>
                <span>Feb</span>
                <span>Mar</span>
                <span>Apr</span>
                <span>May</span>
                <span>Jun</span>
                <span>Jul</span>
                <span>Aug</span>
                <span>Sep</span>
                <span>Oct</span>
                <span>Nov</span>
              </div>
              
              <button className="text-blue-600 dark:text-blue-400 text-sm font-semibold hover:underline flex items-center gap-1">
                View full report <ChevronRight className="w-4 h-4" />
              </button>
            </motion.div>
            
            {/* Right - Leaderboard */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
              viewport={{ once: true, margin: "-50px" }}
              className="bg-white dark:bg-[#020817] rounded-2xl p-8 border border-slate-200 dark:border-slate-800 shadow-sm"
            >
              <h3 className="text-xl font-semibold mb-8 text-slate-900 dark:text-slate-50">Top performing regions</h3>
              
              {/* Table header */}
              <div className="grid grid-cols-4 gap-4 text-xs font-semibold tracking-wider text-slate-500 dark:text-slate-400 pb-4 border-b border-slate-200 dark:border-slate-800 uppercase">
                <span>Rank</span>
                <span>Region</span>
                <span>Schools</span>
                <span>Growth</span>
              </div>
              
              {/* Table rows */}
              <div className="divide-y divide-slate-200 dark:divide-slate-800">
                {[
                  { rank: 1, name: "Nairobi", schools: "127", growth: "+23%" },
                  { rank: 2, name: "Lagos", schools: "98", growth: "+18%" },
                  { rank: 3, name: "Accra", schools: "76", growth: "+15%" },
                  { rank: 4, name: "Kampala", schools: "54", growth: "+12%" },
                  { rank: 5, name: "Dar es Salaam", schools: "41", growth: "+9%" },
                ].map((row, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: i * 0.1, ease: "easeOut" }}
                    viewport={{ once: true }}
                    className="grid grid-cols-4 gap-4 py-4 text-sm items-center hover:bg-slate-50 dark:hover:bg-slate-900/50 transition-colors rounded-lg px-2 -mx-2"
                  >
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-50 font-bold text-xs">{row.rank}</span>
                    <span className="font-medium text-slate-900 dark:text-slate-50">{row.name}</span>
                    <span className="text-slate-600 dark:text-slate-400">{row.schools}</span>
                    <span className="text-emerald-600 dark:text-emerald-400 font-medium bg-emerald-50 dark:bg-emerald-900/20 px-2 py-0.5 rounded-full w-fit text-xs">{row.growth}</span>
                  </motion.div>
                ))}
              </div>
              
              <button className="text-blue-600 dark:text-blue-400 text-sm font-semibold hover:underline mt-6 flex items-center gap-1">
                Show all regions <ChevronRight className="w-4 h-4" />
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <Features />

      {/* Workflows Section */}
      <section id="workflows" className="py-24 lg:py-32 px-6 lg:px-8 bg-slate-50 dark:bg-slate-900/30 border-y border-slate-200 dark:border-slate-800/50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Content */}
            <div>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="text-3xl sm:text-4xl font-bold tracking-tight mb-6 text-slate-900 dark:text-slate-50"
              >
                Workflows that <span className="text-blue-600 dark:text-blue-400 italic">just work</span>
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
                className="text-lg text-slate-600 dark:text-slate-400 mb-8 leading-relaxed"
              >
                Automate repetitive tasks and let your staff focus on what matters most - 
                educating students. From attendance to report cards, everything flows seamlessly.
              </motion.p>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
                className="space-y-5"
              >
                {[
                  "Automated attendance tracking and notifications",
                  "One-click report card generation",
                  "Seamless fee collection and reminders",
                  "Integrated parent-teacher communication"
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-4 p-3 rounded-lg hover:bg-white dark:hover:bg-slate-800/50 transition-colors border border-transparent hover:border-slate-200 dark:hover:border-slate-800">
                    <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <span className="text-base font-medium text-slate-700 dark:text-slate-300">{item}</span>
                  </div>
                ))}
              </motion.div>
            </div>
            
            {/* Image placeholder */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#020817] overflow-hidden shadow-2xl shadow-slate-200/50 dark:shadow-none"
            >
              <img 
                src={resolvedTheme === 'dark' ? dashboardDark : dashboardLight}
                alt="Workflow automation"
                className="w-full"
                data-testid="workflows-screenshot"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-24 lg:py-32 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center max-w-2xl mx-auto mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="text-3xl sm:text-4xl font-bold tracking-tight mb-4 text-slate-900 dark:text-slate-50"
            >
              Loved by educators
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="text-lg text-slate-600 dark:text-slate-400"
            >
              See what school administrators are saying about Sonoma.
            </motion.p>
          </div>
          
          {/* Testimonials Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                quote: "Sonoma transformed our school administration. We've reduced paperwork by 80% and improved parent communication significantly.",
                name: "Sarah Johnson",
                role: "Principal",
                school: "Greenwood High School"
              },
              {
                quote: "The attendance tracking system is phenomenal. Real-time notifications help us ensure student safety and engagement every day.",
                name: "Michael Chen",
                role: "IT Director",
                school: "Riverside Academy"
              },
              {
                quote: "Implementation was seamless. The support team guided us through every step, and our teachers adapted quickly to the new system.",
                name: "Emily Peterson",
                role: "Superintendent",
                school: "Lincoln District"
              },
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="flex flex-col justify-between rounded-2xl bg-white dark:bg-[#020817] border border-slate-200 dark:border-slate-800 p-8 shadow-sm hover:shadow-md transition-shadow"
                data-testid={`testimonial-card-${index}`}
              >
                <blockquote className="text-lg text-slate-700 dark:text-slate-300 mb-8 leading-relaxed italic">
                  "{testimonial.quote}"
                </blockquote>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-700 dark:text-blue-300 font-bold text-lg">
                    {testimonial.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900 dark:text-slate-50">{testimonial.name}</div>
                    <div className="text-sm text-slate-500 dark:text-slate-400">
                      {testimonial.role}, {testimonial.school}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <Pricing />

      {/* FAQ Section */}
      <section id="faq" className="py-24 lg:py-32 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="text-3xl sm:text-4xl font-bold tracking-tight mb-4 text-slate-900 dark:text-slate-50"
            >
              Frequently asked questions
            </motion.h2>
          </div>
          
          <div className="grid gap-6 md:grid-cols-2">
            {[
              {
                q: "How long does implementation take?",
                a: "Most schools are up and running within 2 weeks. Our team handles data migration and provides comprehensive training for your staff."
              },
              {
                q: "Is my data secure?",
                a: "Absolutely. We use enterprise-grade encryption, regular security audits, and are fully FERPA compliant. Your data is backed up daily."
              },
              {
                q: "Can I integrate with existing systems?",
                a: "Yes, Sonoma integrates with popular SIS systems, payment gateways, and communication tools. Custom integrations are available on Enterprise plans."
              },
              {
                q: "What support do you offer?",
                a: "All plans include email support. Professional and Enterprise plans include priority phone support and a dedicated success manager."
              },
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#020817] p-8 hover:border-blue-300 dark:hover:border-blue-700 transition-colors"
                data-testid={`faq-item-${index}`}
              >
                <h3 className="text-lg font-semibold mb-3 text-slate-900 dark:text-slate-50">{faq.q}</h3>
                <p className="text-base text-slate-600 dark:text-slate-400 leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 lg:py-32 px-6 lg:px-8 bg-slate-50/50 dark:bg-slate-900/50">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
              Ready to get started?
            </h2>
            <p className="text-slate-500 dark:text-slate-400 mb-8 max-w-lg mx-auto">
              Join 500+ schools already using Sonoma to streamline their operations.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" className="text-sm font-medium rounded-lg px-6 h-11 bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-600 dark:text-white dark:hover:bg-blue-500 shadow-lg shadow-blue-500/20" data-testid="button-cta-primary">
                Start free trial
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button size="lg" variant="outline" className="text-sm font-medium rounded-lg px-6 h-11 bg-white dark:bg-slate-950 border-slate-200 text-slate-900 hover:bg-slate-100 dark:border-slate-800 dark:text-slate-50 dark:hover:bg-slate-800" data-testid="button-cta-secondary">
                Talk to sales
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <LandingFooter />
    </div>
  );
}
